import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import moment from 'moment';

import { areUserRolesIn } from '../../../helpers';
import CardSkeletonLoader from './CardSkeletonLoader';
import SupervisionItem from './SupervisionItem';
import { FORTA_BLUE } from '../../../utils/colors';
import { Roles, SupervisionType } from '../../../constants';

import type {
  IBtSupervision,
  IClientSupervision,
  IGroupSupervision,
  IParentSupervision,
  SupervisionHistoryProps,
} from '../../../utils/types';

type ISupervisionNoteTab = {
  title: string;
  id: SupervisionType;
};

const supervisionNoteTabs: ISupervisionNoteTab[] = [
  { title: 'Client', id: SupervisionType.Client },
  { title: 'BT', id: SupervisionType.Bt },
  { title: 'Parent', id: SupervisionType.Parent },
  { title: 'Group training', id: SupervisionType.GroupParent },
];

function RecentSupervisions(props: SupervisionHistoryProps) {
  const {
    parentSupervisions,
    clientSupervisions,
    btSupervisions,
    groupSupervisions,
    loading = false,
  } = props;
  const [supervisionType, setSupervisionType] = useState<SupervisionType>(SupervisionType.Client);
  const [currentSupervisions, setSupervisions] = useState<
    IClientSupervision[] | IParentSupervision[] | IBtSupervision[] | IGroupSupervision[]
  >(clientSupervisions.slice(0, 4) || []);
  const { user } = useSelector((state: any) => state.auth);

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSupervisionType(supervisionNoteTabs[newValue].id);
  };

  useEffect(() => {
    switch (supervisionType) {
      case SupervisionType.Client:
        setSupervisions(
          clientSupervisions
            .slice()
            .sort(
              (a: IClientSupervision, b: IClientSupervision) =>
                moment(b?.date_start).unix() - moment(a?.date_start).unix(),
            )
            .slice(0, 4) || [],
        );
        break;
      case SupervisionType.Bt:
        setSupervisions(
          btSupervisions
            .slice()
            .sort(
              (a: IBtSupervision, b: IBtSupervision) =>
                moment(b?.date_start).unix() - moment(a?.date_start).unix(),
            )
            .slice(0, 4) || [],
        );
        break;
      case SupervisionType.Parent:
        setSupervisions(
          parentSupervisions
            .slice()
            .sort(
              (a: IParentSupervision, b: IParentSupervision) =>
                moment(b?.date_start).unix() - moment(a?.date_start).unix(),
            )
            .slice(0, 4) || [],
        );
        break;
      case SupervisionType.GroupParent:
        setSupervisions(
          groupSupervisions
            .slice()
            .sort(
              (a: IGroupSupervision, b: IGroupSupervision) =>
                moment(b?.date_start).unix() - moment(a?.date_start).unix(),
            )
            .slice(0, 4) || [],
        );
        break;
      default:
        break;
    }
  }, [supervisionType, clientSupervisions, parentSupervisions, btSupervisions, groupSupervisions]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 1,
          position: 'relative',
        }}
      >
        <Grid container spacing={1}>
          {areUserRolesIn(user?.roles, [
            Roles.Bcba,
            Roles.ClientSuccessManager,
            Roles.Biller,
            Roles.InvoiceCoordinator,
            Roles.Admin,
            Roles.AssistantAssessor,
          ]) && (
            <Tabs
              value={supervisionNoteTabs.findIndex((tab) => tab.id === supervisionType)}
              onChange={handleChange}
              aria-label="Supervision tabs"
              sx={{
                my: 1,
                minHeight: 32,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 400,
                  minHeight: 32,
                  color: FORTA_BLUE.primary,
                },
              }}
            >
              {supervisionNoteTabs.map((tabData, index) => (
                <Tab
                  sx={{
                    minHeight: 32,
                    padding: '0 16px',
                  }}
                  key={tabData.id}
                  label={tabData.title}
                  {...a11yProps(index)}
                  data-testid={`supervisionTab_${tabData.title.replace(/ /g, '_')}`}
                />
              ))}
            </Tabs>
          )}
        </Grid>
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        {loading ? (
          <Grid container spacing={{ xs: 0.5, sm: 2, md: 2 }}>
            {[...Array(4)].map((_, index) => (
              <Grid key={index} item xs={12} sm={6} md={4} lg={3} mb={1}>
                <CardSkeletonLoader supervisionLoader />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={{ xs: 0.5, sm: 2, md: 2 }}>
            {currentSupervisions.length ? (
              currentSupervisions.map(
                (
                  supervision:
                    IClientSupervision | IParentSupervision | IBtSupervision | IGroupSupervision,
                  _index: number,
                ) => (
                  <Grid key={supervision.id} item xs={12} sm={6} md={4} lg={3} mb={1}>
                    <SupervisionItem supervision={supervision} type={supervisionType} />
                  </Grid>
                ),
              )
            ) : (
              <Typography mt={2} ml={2}>
                There are no supervision notes to display.
              </Typography>
            )}
          </Grid>
        )}
      </Box>
    </Box>
  );
}

export default RecentSupervisions;
